import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);
  
  const isActive = (path) => location.pathname === path;

  const handleEstimationClick = () => {
    navigate('/estimation');
    // Ensure scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF] flex flex-col">
      {/* Navigation */}
      <nav 
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E5E2DB]"
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <img 
                src="/logo-main.png" 
                alt="Homely Conciergerie" 
                className="h-[52px] w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors ${
                  isActive('/') ? 'text-[#8A9474]' : 'text-[#2B2B2B] hover:text-[#8A9474]'
                }`}
                data-testid="nav-home"
              >
                Accueil
              </Link>
              <Link
                to="/services"
                className={`text-sm font-medium transition-colors ${
                  isActive('/services') ? 'text-[#8A9474]' : 'text-[#2B2B2B] hover:text-[#8A9474]'
                }`}
                data-testid="nav-services"
              >
                Services
              </Link>
              <Link
                to="/nos-villes"
                className={`text-sm font-medium transition-colors ${
                  location.pathname.includes('/nos-villes') || location.pathname.includes('/gestion-locative') ? 'text-[#8A9474]' : 'text-[#2B2B2B] hover:text-[#8A9474]'
                }`}
                data-testid="nav-villes"
              >
                Nos Villes
              </Link>
              <Link
                to="/blog"
                className={`text-sm font-medium transition-colors ${
                  location.pathname.includes('/blog') ? 'text-[#8A9474]' : 'text-[#2B2B2B] hover:text-[#8A9474]'
                }`}
                data-testid="nav-blog"
              >
                Blog
              </Link>
              <Button
                onClick={handleEstimationClick}
                className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-4 sm:px-6 py-2 text-sm"
                data-testid="nav-cta-estimation"
              >
                Estimer mon bien
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-[#E5E2DB] pt-4">
              <div className="flex flex-col gap-4">
                <Link
                  to="/"
                  className="text-sm font-medium text-[#2B2B2B] hover:text-[#8A9474]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Accueil
                </Link>
                <Link
                  to="/services"
                  className="text-sm font-medium text-[#2B2B2B] hover:text-[#8A9474]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link
                  to="/nos-villes"
                  className="text-sm font-medium text-[#2B2B2B] hover:text-[#8A9474]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Nos Villes
                </Link>
                <Link
                  to="/blog"
                  className="text-sm font-medium text-[#2B2B2B] hover:text-[#8A9474]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/faq"
                  className="text-sm font-medium text-[#2B2B2B] hover:text-[#8A9474]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Button
                  onClick={() => {
                    handleEstimationClick();
                    setMobileMenuOpen(false);
                  }}
                  className="bg-[#8A9474] hover:bg-[#7A8464] text-white w-full"
                >
                  Estimer mon bien
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#2B2B2B] text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <img 
                src="/logo-main.png" 
                alt="Homely Conciergerie" 
                className="h-10 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-sm text-gray-400">
                Gestion complète de votre Airbnb en Suisse romande
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Accueil
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/nos-villes" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Nos Villes
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/estimation" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Estimation Gratuite
                  </Link>
                </li>
              </ul>
            </div>

            {/* Villes */}
            <div>
              <h4 className="font-semibold mb-4">Nos Destinations</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/gestion-locative/verbier" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Verbier
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/lausanne" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Lausanne
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/geneve" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Genève
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/montreux" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Montreux
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/crans-montana" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Crans-Montana
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/yverdon-les-bains" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Yverdon-les-Bains
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/fribourg" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Fribourg
                  </Link>
                </li>
                <li>
                  <Link to="/gestion-locative/vevey" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Vevey
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Articles Populaires</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/blog/fiscalite-location-saisonniere-suisse" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Fiscalité Location Saisonnière
                  </Link>
                </li>
                <li>
                  <Link to="/blog/location-saisonniere-vs-longue-duree" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Saisonnière vs Longue Durée
                  </Link>
                </li>
                <li>
                  <Link to="/blog/photos-airbnb-professionnelles" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Photos Airbnb Pro
                  </Link>
                </li>
                <li>
                  <Link to="/blog/estimation-immobiliere-location-saisonniere" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    Estimation Immobilière
                  </Link>
                </li>
                <li>
                  <Link to="/legal/mentions" className="text-gray-400 hover:text-[#8A9474] transition-colors mt-4 block">
                    Mentions légales
                  </Link>
                </li>
                <li>
                  <Link to="/legal/cgv" className="text-gray-400 hover:text-[#8A9474] transition-colors">
                    CGV
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="font-medium text-white">Homely Solutions Sàrl</li>
                <li>Rue Jean André Venel 4</li>
                <li>1400 Yverdon-les-Bains</li>
                <li className="pt-2">
                  <a href="mailto:info@homely-solutions.com" className="hover:text-[#8A9474] transition-colors">
                    info@homely-solutions.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>© {new Date().getFullYear()} Homely Solutions Sàrl. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
